<template>
  <div
    class="contact-item"
    @click="openContact($event)"
    @contextmenu.prevent.stop="showContextMenu"
  >
    <div class="contact-content">
      <Avatar class="avatar" :personish="avatarData" size="small" />
      <div class="contact-info">
        <div class="contact-name">{{ contact.full_name }}</div>
        <div class="contact-email">{{ contact.email }}</div>
      </div>
      <div v-if="contact.hasNotes" class="has-notes-indicator">
        <NotesIcon class="notes-icon" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";

import Avatar, { Personish } from "@/components/elements/Avatar.vue";
import NotesIcon from "@/components/icons/NotesIcon.vue";
import * as inject from "@/components/inject";
import { navigateToObject } from "@/utils/navigate";

import type { Contact } from "./ContactsList.vue";

export default defineComponent({
  name: "ContactItem",
  components: {
    Avatar,
    NotesIcon,
  },
  props: {
    contact: {
      type: Object as PropType<Contact>,
      required: true,
    },
  },
  setup(props) {
    const router = inject.router();
    const controller = inject.controller();
    const contextMenu = inject.contextMenu();

    const avatarData = computed<Personish>(() => {
      return {
        kind: "person",
        id: props.contact.id,
        email: props.contact.email,
        otherEmails: [],
        owner: "",
        name: {
          display: props.contact.full_name,
          given: props.contact.given_name,
          family: props.contact.family_name,
        },
        phones: [],
        organizations: [],
        source: "other",
        photo: props.contact.avatar,
      };
    });

    const openContact = (mouseEvent?: MouseEvent) => {
      navigateToObject(
        router,
        controller,
        {
          kind: "people",
          id: props.contact.id,
        },
        mouseEvent
      );
    };

    const showContextMenu = async (e: MouseEvent) => {
      const data = await controller.people.get(props.contact.id);
      data.release();
      if (data.value) {
        contextMenu.value.show({ type: "people", value: data.value }, e);
      }
    };

    return {
      avatarData,
      openContact,
      showContextMenu,
    };
  },
});
</script>

<style scoped lang="scss">
.contact-item {
  padding-block: 10px;
  padding-inline: 16px;
  border-radius: $border-radius-m;
  box-sizing: border-box;
  @include transition(background-color 100ms ease-out);

  &:hover {
    @include themed() {
      background-color: t($grey-05);
    }
  }

  &:active {
    @include themed() {
      background-color: t($grey-10);
    }
  }

  .contact-content {
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;

    .avatar {
      flex-shrink: 0;
    }

    .contact-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .contact-name {
        @extend %font-body-medium;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        @include themed() {
          color: t($primary);
        }
      }

      .contact-email {
        @extend %font-caption-regular;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        @include themed() {
          color: t($grey-50);
        }
      }
    }

    .has-notes-indicator {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;

      .notes-icon {
        width: 16px;
        height: 16px;

        @include themed() {
          .fill {
            fill: t($secondary);
          }
          .stroke {
            stroke: t($secondary);
          }
        }
      }
    }
  }
}
</style>
